import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { endpoints } from '../../api/api';
import { Lead, SalesLeadsResponse, StatusSummary, SalesStatus } from './types';
import { STEP_OPTIONS, SALES_STATUS_OPTIONS, BOOK_STATUS_OPTIONS, SortField, SortDir } from './stepLabels';
import LeadsTable from './components/LeadsTable';
import ContactStatusModal from './components/ContactStatusModal';
import StatTileRow from './components/StatTileRow';

const EMPTY_SUMMARY: StatusSummary = { not_contacted: 0, contacted: 0, follow_up: 0, not_interested: 0 };

const POLL_INTERVAL_MS = 15 * 1000;
const SEARCH_DEBOUNCE_MS = 400;

// Sensible first-click direction per field — alphabetical fields start
// ascending, "Book Last Updated" starts most-recent-first, but "Last
// Contacted" starts ascending (never-contacted/longest-overdue first) since
// that's the actual call-queue priority order, not recency.
const DEFAULT_DIR_FOR_FIELD: Record<SortField, SortDir> = {
  name: 'asc',
  phoneNumber: 'asc',
  updatedAt: 'desc',
  salesUpdatedAt: 'asc',
};

const SalesDashboard: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const stepFilter = searchParams.get('currentStep') || '';
  const bookStatusFilter = searchParams.get('status') || '';
  const salesStatusFilter = searchParams.get('salesStatus') || '';
  const searchQuery = searchParams.get('search') || '';
  // Default to the call-queue view: never-contacted / longest-overdue leads first.
  const sortBy = (searchParams.get('sortBy') || 'salesUpdatedAt') as SortField;
  const sortDir = (searchParams.get('sortDir') || 'asc') as SortDir;
  const currentPage = parseInt(searchParams.get('page') || '1', 10);

  const [searchInput, setSearchInput] = useState(searchQuery);
  const [leads, setLeads] = useState<Lead[]>([]);
  const [loading, setLoading] = useState(true);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [statusSummary, setStatusSummary] = useState<StatusSummary>(EMPTY_SUMMARY);
  const [activeLead, setActiveLead] = useState<Lead | null>(null);

  // Guards the poll tick so an in-flight contact-status edit can't be
  // clobbered by a background refetch overwriting the list mid-edit.
  const modalOpenRef = useRef(false);
  useEffect(() => { modalOpenRef.current = !!activeLead; }, [activeLead]);

  // Debounce the search box before it hits the URL/API — typing shouldn't
  // fire a request per keystroke.
  useEffect(() => {
    const handle = setTimeout(() => {
      if (searchInput === searchQuery) return;
      setSearchParams(prev => {
        const next = new URLSearchParams(prev);
        if (searchInput) next.set('search', searchInput); else next.delete('search');
        next.delete('page');
        return next;
      });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(handle);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchInput]);

  const fetchLeads = useCallback(async (showSpinner = true) => {
    if (showSpinner) setLoading(true);
    try {
      const params: Record<string, string> = {
        page: String(currentPage), limit: '20', sortBy, sortDir,
      };
      if (stepFilter) params.currentStep = stepFilter;
      if (bookStatusFilter) params.status = bookStatusFilter;
      if (salesStatusFilter) params.salesStatus = salesStatusFilter;
      if (searchQuery) params.search = searchQuery;

      const response = await axios.get<SalesLeadsResponse>(endpoints.getSalesLeads, { params });
      setLeads(response.data.leads || []);
      setTotalPages(response.data.totalPages || 1);
      setTotalCount(response.data.count || 0);
      setStatusSummary(response.data.statusSummary || EMPTY_SUMMARY);
    } catch (error) {
      console.error('Failed to fetch sales leads:', error);
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, [stepFilter, bookStatusFilter, salesStatusFilter, searchQuery, sortBy, sortDir, currentPage]);

  useEffect(() => {
    fetchLeads();
  }, [fetchLeads]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (modalOpenRef.current) return;
      fetchLeads(false);
    }, POLL_INTERVAL_MS);
    return () => clearInterval(interval);
  }, [fetchLeads]);

  const handleLogout = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
    navigate('/admin/login');
  };

  const handleFilterChange = (key: 'currentStep' | 'status' | 'salesStatus') => (e: React.ChangeEvent<HTMLSelectElement>) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (e.target.value) next.set(key, e.target.value); else next.delete(key);
      next.delete('page');
      return next;
    });
  };

  const handleTileSelect = (statusValue: SalesStatus) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (salesStatusFilter === statusValue) next.delete('salesStatus'); else next.set('salesStatus', statusValue);
      next.delete('page');
      return next;
    });
  };

  const handleSortChange = (field: SortField) => {
    setSearchParams(prev => {
      const next = new URLSearchParams(prev);
      if (field === sortBy) {
        next.set('sortDir', sortDir === 'asc' ? 'desc' : 'asc');
      } else {
        next.set('sortBy', field);
        next.set('sortDir', DEFAULT_DIR_FOR_FIELD[field]);
      }
      next.delete('page');
      return next;
    });
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 py-4 flex justify-between items-center">
          <h1 className="text-2xl font-bold text-gray-900">Sales Leads</h1>
          <button
            onClick={handleLogout}
            className="px-4 py-1.5 text-sm text-gray-500 hover:text-gray-900 transition-colors"
          >
            Logout
          </button>
        </div>
      </header>
      <main className="px-4 py-8">
        <StatTileRow summary={statusSummary} activeStatus={salesStatusFilter} onSelect={handleTileSelect} />
        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between gap-3 flex-wrap">
            <h2 className="text-lg font-semibold text-gray-900">
              Leads ({loading ? '…' : totalCount})
            </h2>
            <div className="flex gap-2 flex-wrap">
              <input
                type="text"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
                placeholder="Search name or phone..."
                className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 w-48"
              />
              <select
                value={stepFilter}
                onChange={handleFilterChange('currentStep')}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {STEP_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <select
                value={bookStatusFilter}
                onChange={handleFilterChange('status')}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {BOOK_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
              <select
                value={salesStatusFilter}
                onChange={handleFilterChange('salesStatus')}
                className="px-3 py-2 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                {SALES_STATUS_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
              </select>
            </div>
          </div>
          {loading
            ? <div className="flex items-center justify-center py-16"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" /></div>
            : <LeadsTable leads={leads} onOpenContact={setActiveLead} sortBy={sortBy} sortDir={sortDir} onSortChange={handleSortChange} />
          }
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <span className="text-sm text-gray-500">Page {currentPage} of {totalPages}</span>
              <div className="flex gap-2">
                <button
                  onClick={() => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('page', String(Math.max(1, currentPage - 1))); return n; })}
                  disabled={currentPage <= 1}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <button
                  onClick={() => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('page', String(Math.min(totalPages, currentPage + 1))); return n; })}
                  disabled={currentPage >= totalPages}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
      {activeLead && (
        <ContactStatusModal
          lead={activeLead}
          onClose={() => setActiveLead(null)}
          onSaved={() => fetchLeads(false)}
          onSwitchLead={setActiveLead}
        />
      )}
    </div>
  );
};

export default SalesDashboard;
